import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, RefreshCw, CheckCircle } from 'lucide-react';
import { apiClient } from '../api';

const SEVERITY_STYLES = {
  CRITICAL: { text: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20' },
  HIGH: { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20' },
  MEDIUM: { text: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
  LOW: { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20' },
};

const Threats = () => {
  const [alerts, setAlerts] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const [resolvedFilter, setResolvedFilter] = useState('0');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [serverFilter, setServerFilter] = useState('all');

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (resolvedFilter !== 'all') {
        params.set('resolved', resolvedFilter);
      }
      if (serverFilter !== 'all') {
        params.set('server_id', serverFilter);
      }

      const res = await apiClient.get(`/api/alerts/cve?${params.toString()}`);
      setAlerts(res.data.cve_alerts || []);
    } catch (err) {
      console.error('Failed to fetch CVE alerts:', err);
      setError(err.response?.data?.error || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [resolvedFilter, serverFilter]);

  const fetchServers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/servers');
      setServers(res.data.servers || []);
    } catch (err) {
      console.error('Failed to fetch servers:', err);
    }
  }, []);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      await apiClient.patch(`/api/alerts/cve/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to resolve alert:', err);
      setError('Failed to resolve alert');
    } finally {
      setResolvingId(null);
    }
  };

  const getServerName = (serverId) => {
    const server = servers.find((s) => s.id === serverId);
    return server ? server.hostname : serverId;
  };

  const filteredAlerts = severityFilter === 'all'
    ? alerts
    : alerts.filter((a) => a.severity === severityFilter);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">CVE Alerts</h1>
          <p className="text-sm text-gray-500 mt-1">Vulnerabilities detected across your assets</p>
        </div>
        <button
          onClick={fetchAlerts}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-500">Filters:</span>

        <select
          value={resolvedFilter}
          onChange={(e) => setResolvedFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
        >
          <option value="0">Unresolved</option>
          <option value="1">Resolved</option>
          <option value="all">All</option>
        </select>

        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
        >
          <option value="all">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>

        <select
          value={serverFilter}
          onChange={(e) => setServerFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
        >
          <option value="all">All Servers</option>
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.hostname}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="p-4 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
          <strong>Error:</strong> {error}
          <button
            onClick={fetchAlerts}
            className="ml-4 text-sm underline hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      <div className="bg-gray-900 border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50">
          <div>
            <p className="text-sm font-semibold text-gray-300">Vulnerability Alerts</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''} shown
              {alerts.length !== filteredAlerts.length && ` of ${alerts.length} total`}
            </p>
          </div>
        </div>

        <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-700/30">
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading CVE alerts...</div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {alerts.length > 0
                ? 'No alerts match the current filters.'
                : 'No CVE alerts found. Alerts are generated during scheduled vulnerability scans.'}
            </div>
          ) : (
            filteredAlerts.map((cve) => {
              const sev = SEVERITY_STYLES[cve.severity] || SEVERITY_STYLES.LOW;
              return (
                <div
                  key={cve.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-800/30 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ShieldAlert className={`w-4 h-4 flex-shrink-0 ${sev.text}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium font-mono text-gray-200 truncate">
                        {cve.cve_id}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {cve.package_name}@{cve.version} &middot; {cve.ecosystem} &middot; {getServerName(cve.server_id)}
                      </p>
                      {cve.description && (
                        <p className="text-xs text-gray-600 truncate mt-0.5" title={cve.description}>
                          {cve.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${sev.bg} ${sev.text} border ${sev.border}`}>
                      {cve.severity}
                    </span>
                    <span className="text-xs font-mono text-gray-400">
                      CVSS {cve.cvss_score ?? 'N/A'}
                    </span>
                    {cve.resolved ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                        Resolved
                      </span>
                    ) : (
                      <button
                        onClick={() => handleResolve(cve.id)}
                        disabled={resolvingId === cve.id}
                        className="text-gray-500 hover:text-emerald-400 transition-colors disabled:opacity-50"
                        title="Resolve alert"
                      >
                        {resolvingId === cve.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Threats;
